import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import BaseLink from 'components/Link/BaseLink';
import { Event } from 'components/Tracking/';
import UAParser from 'ua-parser-js';
import Cookies from 'js-cookie';
import { pclnPlayStoreLink, pclnAppStoreLink } from 'constants/';
import { ThemeProvider } from 'pcln-design-system';
import { getReferralCookieValues } from 'server/middleware/Referral';
import { buildURL } from 'utils/BuildURL';
import config from 'config';
import { withAMP } from 'containers/AMP';
import PropTypes from 'prop-types';

const Banner = styled.div`
    background-color: ${themeGet('colors.darkBlue')};
    * {
        box-sizing: border-box;
    }
`;

const BannerContent = styled.div`
    color: #ffffff;
    font-family: ${themeGet('font')};
    font-size: ${themeGet('fontSizes.0')}px;
    line-height: 1.13;
    padding: ${themeGet('space.3')}px;
    display: table;
    width: 100%;
    min-width: 308px;
`;

const BannerCloseWrapper = styled.div`
    display: table-cell;
    vertical-align: middle;
`;

const BannerIconContainer = styled.div`
    padding: 0 ${themeGet('space.1')}px 0 ${themeGet('space.2')}px;
    display: table-cell;
    vertical-align: top;
    width: 59px;
`;

const BannerTitle = styled.span`
    margin: 0 ${themeGet('space.2')}px;
    width: 40%;
    min-width: 110px;
    display: table-cell;
    vertical-align: top;
`;

const BannerButtonContainer = styled.div`
    display: table-cell;
    vertical-align: middle;
    margin-left: ${themeGet('space.2')}px;
    float: right;
`;

const BannerClose = styled.button`
    background-color: transparent;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cg fill='none' fill-rule='evenodd' stroke='%23CAE4F2' stroke-linecap='round' stroke-width='2'%3E%3Cpath d='M1.25 1.25l9.5 9.5M1.25 10.75l9.5-9.5'/%3E%3C/g%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center center;
    width: 12px;
    height: 12px;
    color: #ffffff;
    font-size: ${themeGet('fontSizes.2')}px;
    display: table-cell;
    vertical-align: middle;
    padding-right: ${themeGet('space.1')}px;
    font-family: ${themeGet('font')};
    font-size: ${themeGet('fontSizes.1')}px;
    cursor: pointer;
    padding: 0;
    border: none;
    outline: none;
`;

const BannerIcon = styled.span`
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath fill='%230A84C1' d='M0 36c0 2.21 1.796 4 4 4h32c2.21 0 4-1.796 4-4V4c0-2.21-1.796-4-4-4H4C1.79 0 0 1.796 0 4v32z'/%3E%3Cpath fill='%23FFF' d='M20.109 10.002c-.766 0-1.557.337-2.156 1.176-.602.81-.873 2.07-.873 3.44 0 1.846.466 2.994 1.009 3.609a2.62 2.62 0 0 0 1.883.84c2.045 0 2.947-2.267 2.947-4.562 0-1.927-.518-3.969-2.238-4.42a2.084 2.084 0 0 0-.572-.083m-3.328-1.287c.135-.167.272-.335.408-.476.71-.7 1.992-1.282 3.575-1.282 3.491 0 6.193 2.71 6.193 7.578 0 2.99-1.284 7.638-6.168 7.638-1.58 0-2.399-.531-3.52-1.231v6.015h-4.226V7.344h3.738v1.37'/%3E%3Cpath fill='%2300B0DE' d='M13.043 33.913h4.638v-3.478h-4.638z'/%3E%3Cpath fill='%2370C9E7' d='M17.681 33.913h4.638v-3.478H17.68z'/%3E%3Cpath fill='%239DD7EB' d='M22.319 33.913h4.638v-3.478h-4.638z'/%3E%3C/g%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center center;
    width: 40px;
    height: 40px;
    display: inline-block;
`;

const BannerContentEmphasize = styled.span`
    font-weight: bold;
    display: block;
    font-size: ${themeGet('fontSizes.2')}px;
    padding-bottom: ${themeGet('space.1')}px;
`;

const BannerButton = styled.div`
    display: inline-block;
    border: solid 1px white;
    padding: 10px 13px;
    line-height: 13px;
`;

const BannerButtonLink = styled(BaseLink)`
    &,
    &:link,
    &:hover,
    &:active,
    &:focus {
        color: #ffffff;
        font-size: ${themeGet('fontSizes.0')}px;
        text-align: center;
        text-decoration: none;
        outline: 0;
        cursor: pointer;
        background: transparent;
    }
`;

const pageKeyMapping = {
    home: null,
    hdcountry: 'SEOCOUNTRY',
    hdregion: 'SEOSTATE',
    hdcity: 'SEOCITY',
    hddetail: 'SEOHOTEL',
    hdreview: 'SEOHOTELREVIEW'
};

class AppBanner extends React.Component {
    constructor(props) {
        super(props);
        if (typeof window !== 'undefined') {
            const deviceDetails = new UAParser(window.navigator.userAgent);
            const device = deviceDetails.getDevice();

            this.state = {
                closed: Cookies.get('hideAppBanner') || false
            };
            this.cookieValues = getReferralCookieValues(Cookies.get('Referral'));
            this.isClient = true;
            if (device.type === 'mobile') {
                const deviceOS = deviceDetails.getOS().name;
                const tealiumData = (window.pclntms && window.pclntms.dataDictionary) || {};
                const appStoreLink = deviceOS === 'Android' ? pclnPlayStoreLink : pclnAppStoreLink;
                const params = {
                    channel: 'email',
                    theme: deviceOS === 'Android' ? 'ai_mwebhp_android' : 'ai_mwebhp_ios',
                    product: 'none',
                    refid: `${config.PCLN_SOURCE_ID}${config.PCLN_REFID}`
                };
                if (this.cookieValues.CLICKID) {
                    params.refclickid = this.cookieValues.CLICKID;
                }
                if (tealiumData.pageType && pageKeyMapping[tealiumData.pageType]) {
                    params.PAGEID = pageKeyMapping[tealiumData.pageType];
                }
                if (tealiumData.destCityId) {
                    params.cityID = tealiumData.destCityId;
                }
                if (tealiumData.hotelId) {
                    params.propertyID = tealiumData.hotelId;
                }
                this.deviceOS = deviceOS;
                this.isMobile = true;
                this.href = buildURL(appStoreLink, params);
            }
        }
    }
    close = () => {
        this.setState({
            closed: true
        });
        Cookies.set('hideAppBanner', true);
    };
    static propTypes = {
        isAMP: PropTypes.bool
    };
    render() {
        if (this.props.isAMP || !this.isClient || !this.isMobile || this.state.closed) {
            return null;
        }

        return ReactDOM.createPortal(
            <ThemeProvider>
                <Banner>
                    <Event selector=".track-app-banner" category="SEO-HOTEL" action="Mobile" label={this.deviceOS} />
                    <BannerContent>
                        <BannerCloseWrapper>
                            <BannerClose aria-label="Close Banner" onClick={this.close} />
                        </BannerCloseWrapper>
                        <BannerIconContainer>
                            <BannerIcon />
                        </BannerIconContainer>
                        <BannerTitle>
                            <BannerContentEmphasize>Priceline App</BannerContentEmphasize>
                            <span>Download for free</span>
                        </BannerTitle>
                        <BannerButtonContainer>
                            <BannerButton className="track-app-banner">
                                <BannerButtonLink href={this.href}>Open App</BannerButtonLink>
                            </BannerButton>
                        </BannerButtonContainer>
                    </BannerContent>
                </Banner>
            </ThemeProvider>,
            document.getElementById('appBanner')
        );
    }
}

export default withAMP(AppBanner);
